import React, { useState, useRef, useEffect } from "react";

/**
 * Reusable neighborhood dropdown with type-to-filter.
 *
 * Props:
 *   neighborhoods     - array of { id, name }
 *   selected          - multi: array of ids, single: single id/string
 *   onChange          - multi: (ids[]) => void, single: (id) => void
 *   multi             - boolean (default true)
 *   defaults          - optional array of default ids (shows "My Defaults" button)
 *   placeholder       - custom placeholder text
 *   label             - label text (default "Neighborhoods")
 *   style             - extra style on outer container
 *   gridColumn        - CSS grid-column value
 */
export default function NeighborhoodSelect({
  neighborhoods = [],
  selected,
  onChange,
  multi = true,
  defaults,
  placeholder,
  label = "Neighborhoods",
  style,
  gridColumn,
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const filterRef = useRef(null);

  // Focus the filter input when dropdown opens
  useEffect(() => {
    if (open && filterRef.current) filterRef.current.focus();
  }, [open]);

  // Clear filter when closing
  useEffect(() => {
    if (!open) setFilter("");
  }, [open]);

  const sorted = [...neighborhoods].sort((a, b) => a.name.localeCompare(b.name));
  const filtered = filter
    ? sorted.filter(n => n.name.toLowerCase().includes(filter.toLowerCase()))
    : sorted;

  // Display text
  const selectedArr = multi ? (selected || []) : [];
  const singleVal = !multi ? selected : null;
  let displayText;
  if (multi) {
    if (selectedArr.length === 0) displayText = placeholder || "All Neighborhoods";
    else if (selectedArr.length === 1) displayText = neighborhoods.find(n => n.id === selectedArr[0])?.name || "1 selected";
    else displayText = `${selectedArr.length} neighborhoods selected`;
  } else {
    if (!singleVal) displayText = placeholder || "All Neighborhoods";
    else displayText = neighborhoods.find(n => String(n.id) === String(singleVal))?.name || "1 selected";
  }

  const hasSelection = multi ? selectedArr.length > 0 : !!singleVal;

  return (
    <div style={{ position: "relative", ...(gridColumn ? { gridColumn } : {}), ...style }}>
      {label && (
        <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
          {label}
        </label>
      )}
      <div
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "1px solid #ced4da",
          borderRadius: "4px",
          fontSize: "13px",
          backgroundColor: hasSelection ? "#e3f2fd" : "white",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "32px",
          boxSizing: "border-box",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {displayText}
        </span>
        <span style={{ marginLeft: "8px", color: "#666" }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1000,
            maxHeight: "350px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Filter input + action buttons */}
          <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
            <input
              ref={filterRef}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter..."
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                padding: "5px 8px",
                fontSize: "13px",
                border: "1px solid #ddd",
                borderRadius: "3px",
                boxSizing: "border-box",
                marginBottom: "6px",
              }}
            />
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(multi ? [] : "");
                }}
                style={{
                  padding: "4px 8px",
                  fontSize: "11px",
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #ddd",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                Clear All
              </button>
              {multi && defaults && defaults.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(defaults);
                  }}
                  style={{
                    padding: "4px 8px",
                    fontSize: "11px",
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #90caf9",
                    borderRadius: "3px",
                    cursor: "pointer",
                  }}
                >
                  My Defaults ({defaults.length})
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div style={{ overflowY: "auto", maxHeight: "280px" }}>
            {filtered.length === 0 && (
              <div style={{ padding: "12px", color: "#999", fontSize: "13px", textAlign: "center" }}>No matches</div>
            )}
            {filtered.map((n) => {
              const isChecked = multi ? selectedArr.includes(n.id) : String(singleVal) === String(n.id);
              return (
                <label
                  key={n.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    cursor: "pointer",
                    backgroundColor: isChecked ? "#e3f2fd" : "transparent",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {multi ? (
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        if (e.target.checked) onChange([...selectedArr, n.id]);
                        else onChange(selectedArr.filter(x => x !== n.id));
                      }}
                      style={{ marginRight: "8px" }}
                    />
                  ) : (
                    <input
                      type="radio"
                      name="neighborhood-select"
                      checked={isChecked}
                      onChange={() => {
                        onChange(n.id);
                        setOpen(false);
                      }}
                      style={{ marginRight: "8px" }}
                    />
                  )}
                  <span style={{ flex: 1 }}>{n.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {open && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
}
